import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { apiService } from '../services/api';
import { Client, UpdateClientForm } from '../types';
import ClientForm from '../components/forms/ClientForm';
import ClientLocationsPanel from '../components/clients/ClientLocationsPanel';

const ClientEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['client', id],
    queryFn: () => apiService.getClient(parseInt(id!)),
    enabled: !!id
  });
  const client = data?.data as Client | undefined;

  const updateClientMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateClientForm }) => {
      console.log('ClientEditPage: updateClientMutation.mutationFn called with:', { id, data });
      return apiService.updateClient(id, data);
    },
    onSuccess: (response) => {
      console.log('ClientEditPage: updateClientMutation.onSuccess called with:', response);
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      queryClient.invalidateQueries({ queryKey: ['client', id] });
      // Show success message
      toast.success(`Client "${response.data?.name || 'Unknown'}" updated successfully!`);
    },
    onError: (error: any) => {
      console.error('ClientEditPage: updateClientMutation.onError called with:', error);
      console.error('ClientEditPage: error.response:', error?.response);
      console.error('ClientEditPage: error.response.data:', error?.response?.data);
      
      // Extract error message from various possible locations
      let errorMessage = 'Unknown error occurred';
      
      if (error?.response?.data) {
        // Try different error message formats
        if (error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.data.errors && Array.isArray(error.response.data.errors)) {
          errorMessage = error.response.data.errors.join(', ');
        } else if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.title) {
          errorMessage = error.response.data.title;
        }
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      toast.error(`Error updating client: ${errorMessage}`);
    }
  });

  if (isLoading) {
    return <div className="p-6">Loading client...</div>;
  }
  if (error || !client) {
    return <div className="p-6">Client not found.</div>;
  }

  const handleUpdate = (form: UpdateClientForm) => {
    console.log('ClientEditPage: handleUpdate called with form:', form);
    console.log('ClientEditPage: client.id:', client.id);
    updateClientMutation.mutate({ id: client.id, data: form });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-4">
        <Button
          variant="outline"
          onClick={() => navigate(-1)}
          className="flex items-center space-x-2"
        >
          <ArrowLeft className="h-4 w-4" />
          <span>Back</span>
        </Button>
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Edit Client</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">Update client info and manage locations</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg px-6 pt-6 pb-3">
          <ClientForm client={client} onSubmit={handleUpdate} isLoading={updateClientMutation.isLoading} />
        </div>
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
          <ClientLocationsPanel clientId={client.id} />
        </div>
      </div>
    </div>
  );
};

export default ClientEditPage;


